import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'
import { viewBadgeDetails } from '../../slice/badgeSlice'

const DEFAULT_BADGE = process.env.PUBLIC_URL + '/assets/images/default-badge.png'

const ViewBadgeDetail = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    const badgeId = location.state?.badgeId
    const { badgeDetail } = useSelector(state => state.badge)

    useEffect(() => {
        if (!badgeId) {
            navigate('/badges')
            return
        }
        dispatch(viewBadgeDetails({ id: badgeId }))
    }, [badgeId, dispatch, navigate])

    if (!badgeDetail) {
        return (
            <div className="text-center py-5">
                <i className="fe-alert-circle" style={{ fontSize: '48px', color: '#dc3545' }}></i>
                <h5 className="mt-3">No data found</h5>
                <button
                    className="btn btn-primary mt-3"
                    onClick={() => navigate('/badges')}
                >
                    Go Back to Badges
                </button>
            </div>
        )
    }

    const {
        name,
        description,
        image,
        min_completed_bookings,
        min_avg_rating,
        min_hours_per_month,
        is_active,
        is_deleted,
        createdAt,
        updatedAt
    } = badgeDetail

    const imageUrl = image || DEFAULT_BADGE

    return (
        <div className="container-fluid px-4 py-3">

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Badge Details</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/badges')}
                >
                    ← Back to List
                </button>
            </div>

            {/* Badge Preview Card */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
                <div className="card-body p-5 text-center">

                    {/* Status */}
                    <div className="mb-3">
                        {is_active === 1 ? (
                            <span className="badge bg-success me-2">Active</span>
                        ) : (
                            <span className="badge bg-secondary me-2">Inactive</span>
                        )}

                        {is_deleted === 1 && (
                            <span className="badge bg-danger">Deleted</span>
                        )}
                    </div>

                    {/* Image */}
                    <img
                        src={imageUrl}
                        alt={name}
                        onError={(e) => {
                            e.currentTarget.onerror = null
                            e.currentTarget.src = DEFAULT_BADGE
                        }}
                        style={{
                            width: '120px',
                            height: '120px',
                            borderRadius: '12px',
                            objectFit: 'contain',
                            border: '1px solid #dee2e6',
                            padding: '10px',
                            backgroundColor: '#fff',
                            marginBottom: '20px'
                        }}
                    />

                    {/* Name */}
                    <h3 className="fw-bold">{name}</h3>

                    {/* Description */}
                    <p className="text-muted" style={{ maxWidth: '500px', margin: '0 auto' }}>
                        {description}
                    </p>
                </div>
            </div>

            {/* Details Section */}
            <div className="row">

                {/* Eligibility Conditions */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-light fw-semibold">
                            Eligibility Criteria
                        </div>
                        <div className="card-body">

                            <div className="mb-3">
                                <small className="text-muted">Min Completed Bookings</small>
                                <h5>{min_completed_bookings || 0}</h5>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted">Min Average Rating</small>
                                <h5>{min_avg_rating || 0} / 5</h5>
                            </div>

                            <div>
                                <small className="text-muted">Min Hours per Month</small>
                                <h5>{min_hours_per_month || 0} hrs</h5>
                            </div>

                        </div>
                    </div>
                </div>

                {/* Status Information */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-light fw-semibold">
                            Status Information
                        </div>
                        <div className="card-body">

                            <div className="mb-3">
                                <small className="text-muted">Active Status</small>
                                <div>
                                    {is_active === 1 ? (
                                        <span className="badge bg-success">Active</span>
                                    ) : (
                                        <span className="badge bg-secondary">Inactive</span>
                                    )}
                                </div>
                            </div>

                            <div>
                                <small className="text-muted">Deletion Status</small>
                                <div>
                                    {is_deleted === 1 ? (
                                        <span className="badge bg-danger">Deleted</span>
                                    ) : (
                                        <span className="badge bg-success">Available</span>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                {/* Record Information */}
                <div className="col-md-6 mb-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-light fw-semibold">
                            Record Information
                        </div>
                        <div className="card-body">

                            <div className="mb-3">
                                <small className="text-muted">Created At</small>
                                <div>
                                    {createdAt
                                        ? moment(createdAt).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </div>
                            </div>

                            <div className="mb-3">
                                <small className="text-muted">Last Updated</small>
                                <div>
                                    {updatedAt
                                        ? moment(updatedAt).local().format('DD MMM YYYY, hh:mm A')
                                        : 'NA'}
                                </div>
                            </div>

                            <div>
                                <small className="text-muted">Last Modified</small>
                                <div>
                                    {updatedAt ? moment(updatedAt).fromNow() : 'NA'}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

            </div>
        </div>
    )
}

export default ViewBadgeDetail